import React, { useMemo, useState } from 'react';
import {
  DescriptionText,
  HorizontalStack,
  PaneFooter,
  PaneHeader,
  PaneSection,
  Surface,
  VerticalStack,
  WizardWorkspace,
} from '@workspace/silk-ui';
import { CheckCircle2, KeyRound, Mail, Server, Shield } from 'lucide-react';

export interface NotificationChannelEditorFormState {
  id?: string;
  channel_key: string;
  display_name: string;
  enabled: boolean;
  default_from_email: string;
  host: string;
  port: string;
  secure: boolean;
  username: string;
  password: string;
}

interface NotificationChannelEditorWizardProps {
  isOpen: boolean;
  form: NotificationChannelEditorFormState;
  setForm: React.Dispatch<React.SetStateAction<NotificationChannelEditorFormState>>;
  saving: boolean;
  onSave: () => Promise<void>;
  onClose: () => void;
}

const inputCls =
  'h-12 w-full rounded-lg border border-border/60 bg-background px-4 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5';

function FormField({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">
        {label}
      </label>
      {children}
      {hint ? <p className="text-[10px] font-medium text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export function NotificationChannelEditorWizard({
  isOpen,
  form,
  setForm,
  saving,
  onSave,
  onClose,
}: NotificationChannelEditorWizardProps) {
  const [activeTabId, setActiveTabId] = useState('identity');
  const mode = form.id ? 'edit' : 'create';

  const summaryItems = useMemo(
    () => [
      { label: 'Channel key', value: form.channel_key || 'Set a key', complete: Boolean(form.channel_key.trim()) },
      { label: 'From email', value: form.default_from_email || 'Set sender', complete: Boolean(form.default_from_email.trim()) },
      { label: 'SMTP host', value: form.host || 'Configure SMTP', complete: Boolean(form.host.trim()) },
      { label: 'Credentials', value: form.username.trim() || form.password ? 'Replacement queued' : mode === 'edit' ? 'Stored secret unchanged' : 'Set credentials', complete: mode === 'edit' || Boolean(form.username.trim() || form.password) },
    ],
    [form.channel_key, form.default_from_email, form.host, form.password, form.username, mode],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <WizardWorkspace
      isOpen={true}
      title={mode === 'edit' ? 'Edit Channel' : 'New Channel'}
      subtitle="Configure the tenant SMTP delivery path used by notification routes."
      tabs={[
        { id: 'identity', label: 'Identity', icon: <Mail className="h-4 w-4" /> },
        { id: 'transport', label: 'Transport', icon: <Server className="h-4 w-4" /> },
        { id: 'credentials', label: 'Credentials', icon: <KeyRound className="h-4 w-4" /> },
        { id: 'review', label: 'Review', icon: <CheckCircle2 className="h-4 w-4" /> },
      ]}
      activeTabId={activeTabId}
      onTabChange={setActiveTabId}
      onSave={onSave}
      onClose={onClose}
      isSaving={saving}
      saveLabel={mode === 'edit' ? 'Save Channel' : 'Create Channel'}
      summaryItems={summaryItems}
    >
        {activeTabId === 'identity' ? (
          <VerticalStack gap={6}>
            <PaneHeader
              title="Channel Identity"
              subtitle="Define the stable tenant-owned email channel key and sender information."
            />
            <div className="px-6 pb-8">
              <PaneSection label="Channel metadata" icon={Mail}>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <FormField label="Display name">
                    <input aria-label="Display name" className={inputCls} value={form.display_name} onChange={(event) => setForm(current => ({ ...current, display_name: event.target.value }))} />
                  </FormField>
                  <FormField label="Channel key" hint="Stable tenant-scoped identifier used by routes.">
                    <input aria-label="Channel key" className={inputCls} value={form.channel_key} onChange={(event) => setForm(current => ({ ...current, channel_key: event.target.value }))} />
                  </FormField>
                  <FormField label="From email">
                    <input aria-label="From email" className={inputCls} value={form.default_from_email} onChange={(event) => setForm(current => ({ ...current, default_from_email: event.target.value }))} />
                  </FormField>
                  <FormField label="Status">
                    <label className="flex h-12 items-center gap-3 rounded-lg border border-border/60 bg-background px-4">
                      <input aria-label="Enabled" type="checkbox" checked={form.enabled} onChange={(event) => setForm(current => ({ ...current, enabled: event.target.checked }))} />
                      <span className="text-sm font-medium text-foreground">{form.enabled ? 'Enabled' : 'Disabled'}</span>
                    </label>
                  </FormField>
                </div>
              </PaneSection>
            </div>
          </VerticalStack>
        ) : null}

        {activeTabId === 'transport' ? (
          <VerticalStack gap={6}>
            <PaneHeader
              title="Transport"
              subtitle="Configure the SMTP endpoint and connection policy for this tenant delivery path."
            />
            <div className="px-6 pb-8">
              <PaneSection label="SMTP endpoint" icon={Server}>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-[minmax(0,1fr)_8rem]">
                  <FormField label="SMTP host">
                    <input aria-label="SMTP host" className={inputCls} value={form.host} onChange={(event) => setForm(current => ({ ...current, host: event.target.value }))} />
                  </FormField>
                  <FormField label="Port">
                    <input aria-label="Port" className={inputCls} value={form.port} onChange={(event) => setForm(current => ({ ...current, port: event.target.value }))} />
                  </FormField>
                </div>
                <div className="mt-6">
                  <FormField label="TLS policy">
                    <label className="flex h-12 items-center gap-3 rounded-lg border border-border/60 bg-background px-4">
                      <input aria-label="Use TLS" type="checkbox" checked={form.secure} onChange={(event) => setForm(current => ({ ...current, secure: event.target.checked }))} />
                      <span className="text-sm font-medium text-foreground">{form.secure ? 'TLS required' : 'Plain or opportunistic TLS'}</span>
                    </label>
                  </FormField>
                </div>
              </PaneSection>
            </div>
          </VerticalStack>
        ) : null}

        {activeTabId === 'credentials' ? (
          <VerticalStack gap={6}>
            <PaneHeader
              title="Credentials"
              subtitle="Credentials are write-only and stored through the notify secret path."
            />
            <div className="px-6 pb-8">
              <PaneSection label="SMTP credentials" icon={KeyRound}>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <FormField label="SMTP username">
                    <input aria-label="SMTP username" className={inputCls} value={form.username} onChange={(event) => setForm(current => ({ ...current, username: event.target.value }))} />
                  </FormField>
                  <FormField
                    label="SMTP password"
                    hint={mode === 'edit' ? 'Leave blank to keep the stored password unchanged.' : undefined}
                  >
                    <input aria-label="SMTP password" type="password" className={inputCls} value={form.password} onChange={(event) => setForm(current => ({ ...current, password: event.target.value }))} />
                  </FormField>
                </div>
              </PaneSection>
            </div>
          </VerticalStack>
        ) : null}

        {activeTabId === 'review' ? (
          <VerticalStack gap={6}>
            <PaneHeader
              title="Review"
              subtitle="Verify the delivery path before saving it for tenant route assignment."
            />
            <div className="px-6 pb-8">
              <PaneSection label="Delivery summary" icon={CheckCircle2}>
                <Surface className="border-border/60 bg-muted/10 p-4">
                  <VerticalStack gap={3}>
                    <HorizontalStack className="justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">Channel</span>
                      <span className="font-bold text-foreground">{form.display_name || 'Unnamed channel'}</span>
                    </HorizontalStack>
                    <HorizontalStack className="justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">Key</span>
                      <span className="font-mono text-foreground">{form.channel_key || 'not-set'}</span>
                    </HorizontalStack>
                    <HorizontalStack className="justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">SMTP endpoint</span>
                      <span className="font-bold text-foreground">{form.host || 'unset'}:{form.port || '1025'}</span>
                    </HorizontalStack>
                    <HorizontalStack className="justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">TLS</span>
                      <span className="font-bold text-foreground">{form.secure ? 'Required' : 'Not required'}</span>
                    </HorizontalStack>
                    <HorizontalStack className="justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">Secret handling</span>
                      <span className="font-bold text-foreground">{form.username.trim() || form.password ? 'Credentials will be written' : mode === 'edit' ? 'Stored secret retained' : 'Credentials missing'}</span>
                    </HorizontalStack>
                  </VerticalStack>
                </Surface>
              </PaneSection>

              <PaneFooter className="mt-8">
                <DescriptionText>
                  Notification channels are tenant-scoped operational infrastructure. Save the channel first, then map it from the Routes workspace.
                </DescriptionText>
                <DescriptionText className="text-[11px]">
                  Stored credentials are never returned to the browser.
                </DescriptionText>
              </PaneFooter>
            </div>
          </VerticalStack>
        ) : null}
    </WizardWorkspace>
  );
}
